/**
 * Save-the-Food Map — public heat map of today's rescued perishables.
 *
 * Route : /food-pulse/impact-map
 * Data  : /api/impact/rescue-map + /api/impact/rescue-stats
 *
 * Uses react-leaflet with plain OSM tiles + CircleMarker whose radius
 * scales with rescued_qty. Left rail lists the top 20 rescuing
 * businesses. Header stats power the "invite a friend" CTA.
 */
import { useEffect, useMemo, useState, useRef } from "react";
import "leaflet/dist/leaflet.css";
import { Loader2, ShareIcon, Trophy, TrendingUp, Users, Zap } from "lucide-react";
import { http } from "@/lib/api";

const GREEN = "#0F3D2E";
const CREAM = "#FBF7EF";
const AMBER = "#E4A140";

function StatCard({ icon: Icon, value, label, color }) {
  return (
    <div className="rounded-2xl p-4 bg-white border" style={{ borderColor: "#E9D8B4" }}>
      <div className="flex items-center gap-2 text-xs text-gray-600"><Icon size={12} color={color || GREEN} /> {label}</div>
      <div className="text-3xl font-black mt-1" style={{ color: color || GREEN }}>{value}</div>
    </div>
  );
}

/**
 * MapOnce — mount Leaflet exactly once, then update markers imperatively.
 * Sidesteps react-leaflet's incompatibility with React 18 StrictMode
 * (which double-invokes effects → "Map container is already initialized").
 */
function MapOnce({ center, cities, maxQty }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  useEffect(() => {
    let map;
    (async () => {
      const L = (await import("leaflet")).default;
      if (!ref.current || mapRef.current) return;
      map = L.map(ref.current, { scrollWheelZoom: true }).setView(center, 4);
      mapRef.current = map;
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(map);
      layerRef.current = L.layerGroup().addTo(map);
    })();
    return () => {
      try { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; layerRef.current = null; } } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      const L = (await import("leaflet")).default;
      const map = mapRef.current;
      const layer = layerRef.current;
      if (!map || !layer) return;
      layer.clearLayers();
      cities.forEach((c) => {
        const scale = Math.min(60, 8 + (c.rescued_qty / maxQty) * 40);
        const marker = L.circleMarker([c.lat, c.lon], {
          radius: scale, color: GREEN, fillColor: "#5EA470", fillOpacity: 0.55, weight: 1,
        });
        marker.bindPopup(
          `<div style="font-size:13px"><b style="color:${GREEN}">${c.city}${c.state ? ", " + c.state : ""}</b><br/>${c.rescued_qty} units rescued<br/><span style="color:#555;font-size:11px">${c.event_count} events · ${c.business_count} stores</span></div>`,
        );
        marker.addTo(layer);
      });
      if (cities.length) map.setView([cities[0].lat, cities[0].lon], map.getZoom() || 4);
    })();
  }, [cities, maxQty]);

  return <div ref={ref} style={{ height: "100%", width: "100%" }} />;
}

export default function FoodPulseImpactMap() {
  const [since, setSince] = useState(24);
  const [map, setMap] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, s] = await Promise.all([
        http.get("/impact/rescue-map", { params: { since_hours: since } }),
        http.get("/impact/rescue-stats", { params: { since_hours: since } }),
      ]);
      setMap(m.data); setStats(s.data);
    } catch (e) { console.warn(e); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [since]);

  const center = useMemo(() => {
    if (map?.cities?.length) return [map.cities[0].lat, map.cities[0].lon];
    return [39.5, -98.5];   // US centroid
  }, [map]);

  const maxQty = useMemo(() => Math.max(1, ...(map?.cities || []).map((c) => c.rescued_qty)), [map]);

  const share = async () => {
    const url = "https://foodpulse.net/food-pulse/impact-map";
    const text = `Together we rescued ${stats?.rescued_qty || 0} units of food from waste in the last ${since}h. Join us on FoodPulse.`;
    try {
      if (navigator.share) await navigator.share({ title: "Save-the-Food Map", text, url });
      else { await navigator.clipboard.writeText(`${text} ${url}`); alert("Copied share text!"); }
    } catch { /* user cancelled */ }
  };

  return (
    <main className="min-h-screen" style={{ background: CREAM }} data-testid="impact-map-page">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <header className="mb-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-3xl font-black flex items-center gap-2" style={{ color: GREEN }}>
                <Trophy /> Save-the-Food Map
              </h1>
              <p className="text-sm text-gray-600 mt-1">Live map of every store rescuing perishable food across FoodPulse. Reduce waste. Make food affordable.</p>
            </div>
            <div className="flex items-center gap-2">
              <select value={since} onChange={(e) => setSince(Number(e.target.value))} className="border rounded-full px-3 py-2 text-sm bg-white" data-testid="since-select">
                <option value={24}>Last 24h</option>
                <option value={72}>Last 3 days</option>
                <option value={168}>Last 7 days</option>
                <option value={720}>Last 30 days</option>
              </select>
              <button onClick={share} className="rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-1" style={{ background: GREEN, color: CREAM }} data-testid="share-btn">
                <ShareIcon size={14} /> Share the impact
              </button>
            </div>
          </div>
        </header>

        {stats && (
          <section className="grid md:grid-cols-4 gap-3 mb-6" data-testid="impact-stats">
            <StatCard icon={Zap} value={stats.rescued_qty} label={`Units rescued (last ${since}h)`} />
            <StatCard icon={TrendingUp} value={stats.events} label="Rescue events" color={AMBER} />
            <StatCard icon={Users} value={stats.businesses} label="Businesses rescuing" />
            <StatCard icon={Trophy} value={stats.deals_live} label="Live deals right now" color={AMBER} />
          </section>
        )}

        {loading && <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto" /></div>}

        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-2xl overflow-hidden border" style={{ borderColor: "#E9D8B4", height: 520 }} data-testid="impact-map">
            <MapOnce center={center} cities={map?.cities || []} maxQty={maxQty} />
          </div>
          <aside className="rounded-2xl bg-white border p-4" style={{ borderColor: "#E9D8B4" }} data-testid="top-business-list">
            <h2 className="text-sm font-bold mb-3" style={{ color: GREEN }}>Top rescuing stores</h2>
            {!map?.top_business?.length ? (
              <div className="text-xs text-gray-600">No rescues logged in this window yet. Check back soon.</div>
            ) : (
              <ul className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
                {map.top_business.slice(0, 20).map((b, i) => (
                  <li key={b.business_id} className="flex items-start gap-2 text-sm border-b pb-2" style={{ borderColor: "#F5E6C4" }} data-testid={`top-biz-${i}`}>
                    <span className="text-lg font-black" style={{ color: AMBER }}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <a href={`/food-pulse/businesses/${b.slug}`} className="font-semibold truncate block" style={{ color: GREEN }}>{b.name}</a>
                      <div className="text-xs text-gray-600 truncate">{b.city || "—"}{b.state ? `, ${b.state}` : ""}</div>
                      <div className="text-xs" style={{ color: GREEN }}><b>{b.rescued_qty}</b> units · {b.events} events</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>

        <div className="rounded-2xl bg-white border p-5 mt-6 text-center" style={{ borderColor: "#E9D8B4" }} data-testid="cta-banner">
          <h3 className="text-lg font-bold" style={{ color: GREEN }}>Every subscriber rescues more food</h3>
          <p className="text-sm text-gray-600 mt-1 mb-3">Share this map with a friend so more households hear about perishables before they go to waste.</p>
          <button onClick={share} className="rounded-full px-5 py-2 text-sm font-semibold" style={{ background: GREEN, color: CREAM }}>Share now</button>
        </div>
      </div>
    </main>
  );
}
